import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { confirmAsync, haptic, notifyHaptic } from "../auth";
import {
  EmptyState,
  ErrorBox,
  SkeletonRows,
  StatusPill,
} from "../components/States";
import { PullToRefresh } from "../components/PullToRefresh";
import { DealEconomicsModal } from "../components/DealEconomicsModal";
import { TONE } from "../theme";
import {
  ActionResponse,
  MeResponse,
  PaymentItem,
  PaymentsResponse,
} from "../types";

interface Props {
  me: MeResponse;
  // Переход в «Экспорт → Распределение» (при нехватке валюты на оплату).
  onGoToDistribute?: () => void;
}

type Filter = "all" | "awaiting_payment" | "awaiting_confirm";
type BulkKind = "mark_paid" | "confirm" | "deny" | "delete";

interface BulkResponse {
  action: string;
  total: number;
  succeeded: number;
  failed: number;
  results: Array<{ deal_id: number; ok: boolean; message: string }>;
}

export function PaymentsPage({ me, onGoToDistribute }: Props) {
  const [data, setData] = useState<PaymentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Отдельно — ошибка нехватки валюты на оплату: показываем с кнопкой
  // перехода в «Распределение».
  const [fxShortage, setFxShortage] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<Record<number, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setFxShortage(null);
    try {
      const resp = await api.get<PaymentsResponse>("/api/payments");
      setData(resp);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const action = async (
    deal_id: number,
    kind: "mark_paid" | "confirm" | "deny",
  ) => {
    setBusy((b) => ({ ...b, [deal_id]: true }));
    haptic("light");
    try {
      const resp = await api.post<ActionResponse>(
        `/api/payments/${deal_id}/${kind}`,
      );
      notifyHaptic("success");
      setToast(resp.message || "Готово");
      setTimeout(() => setToast(null), 2500);
      await refresh();
    } catch (e) {
      notifyHaptic("error");
      const msg =
        e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e);
      // Нехватка валюты на оплату (409 + характерное сообщение движка) —
      // показываем отдельно, с кнопкой перехода в «Распределение».
      const isFxShortage =
        kind === "mark_paid" &&
        e instanceof ApiError &&
        e.status === 409 &&
        /Недостаточно валюты/.test(e.message);
      if (isFxShortage) setFxShortage(msg);
      else setError(msg);
    } finally {
      setBusy((b) => ({ ...b, [deal_id]: false }));
    }
  };

  const bulkAction = async (
    kind: BulkKind,
    opts: { force?: boolean; detach?: boolean; keepSelection?: boolean } = {},
  ): Promise<BulkResponse | null> => {
    const { force = false, detach = false, keepSelection = false } = opts;
    const ids = [...selected];
    if (ids.length === 0) return null;
    setBulkBusy(true);
    setError(null);
    haptic("medium");
    try {
      const resp = await api.post<BulkResponse>("/api/payments/bulk", {
        ids,
        action: kind,
        force,
        detach,
      });
      notifyHaptic(resp.failed > 0 ? "warning" : "success");
      // Если были ошибки — показываем причину (сообщение первой неуспешной).
      const firstFail = resp.results.find((r) => !r.ok);
      if (resp.failed > 0 && firstFail) {
        setError(`Не выполнено (${resp.failed}): ${firstFail.message}`);
      }
      setToast(
        `Готово: ${resp.succeeded} из ${resp.total}` +
          (resp.failed ? `, ошибок ${resp.failed}` : ""),
      );
      setTimeout(() => setToast(null), 3500);
      if (!keepSelection) exitSelect();
      await refresh();
      return resp;
    } catch (e) {
      notifyHaptic("error");
      setError(e instanceof ApiError ? e.message : String(e));
      return null;
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmAndDelete = async () => {
    const n = selected.size;
    if (n === 0) return;
    const ok = await confirmAsync(
      `Удалить ${n} заявку(и) безвозвратно? Действие нельзя отменить.`,
    );
    if (!ok) return;
    const resp = await bulkAction("delete", { keepSelection: true });
    // Часть отклонена из-за распределённого FX-лота?
    const fxBlocked = resp?.results.some(
      (r) => !r.ok && r.message.includes("FX-лот"),
    );
    const isAdmin = me.permissions.agents_view; // agents_view = админ
    if (fxBlocked && isAdmin) {
      // Шаг 1 (штатный): отвязать лот — валюта остаётся в общем пуле.
      const detachOk = await confirmAsync(
        "По части заявок есть распределённый лот валюты. ОТВЯЗАТЬ лот " +
          "(валюта вернётся в общий пул, себестоимость сохранится) и удалить " +
          "заявку?",
      );
      if (detachOk) {
        await bulkAction("delete", { detach: true, keepSelection: true });
      } else {
        // Шаг 2 (крайность): стереть вместе с FX-данными (тестовый мусор).
        const forceOk = await confirmAsync(
          "Иначе удалить заявку ВМЕСТЕ с FX-данными (лот и операции будут " +
            "стёрты)? Только для ошибочных/тестовых заявок.",
        );
        if (forceOk) await bulkAction("delete", { force: true, keepSelection: true });
      }
    }
    exitSelect();
  };

  const filteredGroups = useMemo(() => {
    if (!data) return [];
    const sNorm = search.trim().toLowerCase();
    return data.groups
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => {
          if (filter !== "all" && it.state !== filter) return false;
          if (!sNorm) return true;
          const hay = [
            String(it.id),
            it.counterparty_name ?? "",
            it.company_name ?? "",
            it.bank_name ?? "",
            it.currency,
          ]
            .join(" ")
            .toLowerCase();
          return hay.includes(sNorm);
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [data, filter, search]);

  const counts = useMemo(() => {
    const c = { all: 0, awaiting_payment: 0, awaiting_confirm: 0 };
    data?.groups.forEach((g) =>
      g.items.forEach((it) => {
        c.all += 1;
        if (it.state === "awaiting_payment") c.awaiting_payment += 1;
        if (it.state === "awaiting_confirm") c.awaiting_confirm += 1;
      }),
    );
    return c;
  }, [data]);

  const canBulk =
    me.permissions.payments_write || me.permissions.payments_confirm;

  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="flex items-baseline justify-between mb-3 gap-2">
        <h1 className="text-xl font-semibold">💳 Реестр платежей</h1>
        {canBulk && (
          <button
            onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
            className="text-tg-link text-sm whitespace-nowrap"
          >
            {selectMode ? "Отмена" : "Выбрать"}
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-3 overflow-x-auto -mx-4 px-4">
        {(["all", "awaiting_payment", "awaiting_confirm"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "px-3 py-1.5 rounded-full text-sm whitespace-nowrap " +
              (filter === f
                ? "bg-tg-button text-tg-buttonText"
                : "bg-tg-secondaryBg text-tg-text")
            }
          >
            {f === "all"
              ? `Все (${counts.all})`
              : f === "awaiting_payment"
              ? `Ждут оплаты (${counts.awaiting_payment})`
              : `У бухгалтера (${counts.awaiting_confirm})`}
          </button>
        ))}
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="🔍 Поиск по №, контрагенту, банку…"
        className="w-full mb-3 px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text border border-transparent focus:outline-none"
      />

      <button
        onClick={() => api.download("payments_xlsx", {}, "payments.xlsx")}
        className="w-full mb-4 px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
      >
        📥 Скачать xlsx
      </button>

      {error && (
        <div className="mb-3">
          <ErrorBox text={error} />
        </div>
      )}

      {fxShortage && (
        <div className="mb-3 rounded-lg border border-amber-500 bg-amber-50 dark:bg-amber-900/30 p-3">
          <div className="text-sm text-amber-900 dark:text-amber-200">
            {fxShortage}
          </div>
          <div className="mt-2 flex gap-2">
            {onGoToDistribute && (
              <button
                onClick={() => {
                  setFxShortage(null);
                  onGoToDistribute();
                }}
                className="px-3 py-1.5 rounded-lg bg-tg-button text-tg-buttonText text-sm"
              >
                📦 Перейти в «Распределение»
              </button>
            )}
            <button
              onClick={() => setFxShortage(null)}
              className="px-3 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}

      {loading && !data && <SkeletonRows rows={5} />}

      {data && data.total_count === 0 && !loading && (
        <EmptyState icon="✅" text="Реестр пуст — все оплачено." />
      )}

      {filteredGroups.map((g) => (
        <section key={`${g.company_id}-${g.bank_id}`} className="mb-5">
          <div className="bg-amber-100 dark:bg-amber-900/30 border-l-4 border-r-4 border-amber-500 rounded-lg px-3 py-2.5 mb-2 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-amber-800 dark:text-amber-300 font-semibold">
              Плательщик
            </div>
            <div className="text-base font-bold text-tg-text leading-tight mt-0.5">
              🏢 {g.company_name || "—"}
            </div>
            <div className="text-sm font-medium text-tg-text mt-0.5">
              🏦 {g.bank_name || "—"}
            </div>
          </div>
          <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
            {g.items.map((it) => (
              <PaymentRow
                key={it.id}
                item={it}
                me={me}
                busy={!!busy[it.id]}
                expanded={expanded.has(it.id)}
                onAction={action}
                onToggle={() => toggleExpand(it.id)}
                selectMode={selectMode}
                selected={selected.has(it.id)}
                onSelect={() => toggleSelect(it.id)}
              />
            ))}
          </div>
          <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
            <div className="text-[11px] uppercase tracking-wider text-emerald-800 dark:text-emerald-300 font-semibold mb-1">
              Сумма по банку
            </div>
            <TotalsList totals={g.subtotal_by_currency} />
          </div>
        </section>
      ))}

      {data && filteredGroups.length === 0 && data.total_count > 0 && (
        <EmptyState
          icon="🔍"
          text="По текущему фильтру ничего не найдено."
          action={{
            label: "Сбросить фильтр",
            onClick: () => {
              setFilter("all");
              setSearch("");
            },
          }}
        />
      )}

      {data && data.total_count > 0 && (
        <div className="mt-4 p-3 bg-indigo-100 dark:bg-indigo-900/40 rounded-xl border border-indigo-300 dark:border-indigo-700">
          <div className="text-[11px] uppercase tracking-wider text-indigo-900 dark:text-indigo-200 font-bold mb-1.5">
            Итого
          </div>
          <TotalsList totals={data.grand_total_by_currency} bold />
        </div>
      )}

      {selectMode && selected.size > 0 && (
        <div
          className="fixed left-3 right-3 mx-auto max-w-md bg-tg-sectionBg border border-tg-secondaryBg rounded-xl px-3 py-2.5 shadow-lg"
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 5rem)" }}
        >
          <div className="text-xs text-tg-hint mb-1.5">
            Выбрано заявок: {selected.size}
          </div>
          <div className="flex flex-wrap gap-2">
            {me.permissions.payments_write && (
              <button
                disabled={bulkBusy}
                onClick={() => bulkAction("mark_paid")}
                className="flex-1 px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
              >
                💳 Оплачено
              </button>
            )}
            {me.permissions.payments_confirm && (
              <>
                <button
                  disabled={bulkBusy}
                  onClick={() => bulkAction("confirm")}
                  className="flex-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm disabled:opacity-50"
                >
                  ✅ Подтвердить
                </button>
                <button
                  disabled={bulkBusy}
                  onClick={() => bulkAction("deny")}
                  className="flex-1 px-3 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50"
                >
                  ❌ Отклонить
                </button>
              </>
            )}
            {me.permissions.payments_delete && (
              <button
                disabled={bulkBusy}
                onClick={() => confirmAndDelete()}
                className="flex-1 px-3 py-2 rounded-lg bg-red-800 text-white text-sm disabled:opacity-50"
              >
                🗑 Удалить
              </button>
            )}
          </div>
        </div>
      )}

      {toast && (
        <div
          className="fixed left-4 right-4 mx-auto max-w-sm bg-tg-text text-tg-bg px-4 py-2.5 rounded-lg text-sm text-center shadow-lg"
          // Над панелью навигации с учётом выреза home-indicator.
          style={{
            bottom: "calc(env(safe-area-inset-bottom) + 5.25rem)",
          }}
        >
          {toast}
        </div>
      )}
    </PullToRefresh>
  );
}

function PaymentRow({
  item,
  me,
  busy,
  expanded,
  onAction,
  onToggle,
  selectMode,
  selected,
  onSelect,
}: {
  item: PaymentItem;
  me: MeResponse;
  busy: boolean;
  expanded: boolean;
  onAction: (id: number, kind: "mark_paid" | "confirm" | "deny") => void;
  onToggle: () => void;
  selectMode: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const arrow = item.deal_type === "import" ? "→" : "←";
  const dl = item.deadline ? formatDate(item.deadline) : "—";
  const canExpand = me.permissions.payments_full_details;
  // Дата фиксации в краткой версии — всегда показываем (для всех ролей).
  const fixedDateLabel = item.fixed_at ? fmtRefDate(item.fixed_at) : null;
  const clickable = selectMode || canExpand;
  return (
    <div
      className={
        "px-4 py-3 transition-colors flex gap-2.5 " +
        (clickable ? "cursor-pointer active:bg-tg-secondaryBg/50 " : "") +
        (selectMode && selected ? "bg-tg-secondaryBg/60" : "")
      }
      onClick={selectMode ? onSelect : canExpand ? onToggle : undefined}
      role={clickable ? "button" : undefined}
    >
      {selectMode && (
        <div
          className={
            "mt-0.5 w-5 h-5 rounded-md border-2 flex items-center " +
            "justify-center text-xs shrink-0 " +
            (selected
              ? "bg-tg-button border-tg-button text-tg-buttonText"
              : "border-tg-hint")
          }
        >
          {selected ? "✓" : ""}
        </div>
      )}
      <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-medium min-w-0 flex-1">
          №{item.id} {arrow}{" "}
          <span className="font-normal">{item.counterparty_name || "—"}</span>
        </div>
        <div className="text-tg-hint text-xs whitespace-nowrap">до {dl}</div>
      </div>
      <div className="mt-1">
        <PaymentStatePill state={item.state} />
      </div>
      <div className="mt-1 flex items-baseline gap-3 text-sm">
        <span className="font-semibold">
          {fmtAmount(item.amount)} {item.currency}
        </span>
        <span className="text-tg-hint">{item.deal_format || ""}</span>
      </div>

      {item.rub_total != null && (
        <div className="mt-1 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-xs">
          <div className="text-tg-hint">
            Платёж ₽: тело{" "}
            <b className="text-tg-text">{fmtAmount(item.rub_body || 0)}</b>
            {" + "}вознагр.{" "}
            <b className="text-tg-text">
              {fmtAmount(item.rub_commission || 0)}
            </b>
            {" = "}
            <b className="text-tg-text">{fmtAmount(item.rub_total)}</b> ₽
          </div>
          {(item.rub_received > 0 ||
            item.rub_debit > 0 ||
            item.rub_credit > 0) && (
            <div className="mt-0.5 text-tg-hint">
              Поступило {fmtAmount(item.rub_received)} ₽
              {item.rub_debit > 0 && (
                <span style={{ color: TONE.danger }}>
                  {" · "}дебиторка {fmtAmount(item.rub_debit)} ₽
                </span>
              )}
              {item.rub_credit > 0 && (
                <span style={{ color: TONE.warning }}>
                  {" · "}переплата {fmtAmount(item.rub_credit)} ₽
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {fixedDateLabel && (
        <div className="mt-0.5 text-[11px] text-tg-hint">
          🔒 Зафиксировано: {fixedDateLabel}
        </div>
      )}

      {item.cross_rate_usd_to_pay != null && (
        <div className="mt-1 text-xs text-tg-hint leading-snug">
          <div>
            💱 1 USD = {item.cross_rate_usd_to_pay.toFixed(4)} {item.currency}
            {" · "}
            1 {item.currency} ={" "}
            {item.cross_rate_usd_to_pay > 0
              ? (1 / item.cross_rate_usd_to_pay).toFixed(4)
              : "—"}{" "}
            USD
          </div>
          {item.rate_ref_date && (
            <div className="text-tg-hint/70">
              ЦБ на {fmtRefDate(item.rate_ref_date)}
            </div>
          )}
        </div>
      )}

      {item.notes && (
        <div className="mt-1.5 px-2 py-1 rounded bg-amber-50 border-l-2 border-amber-400 text-xs text-amber-900">
          💬 {item.notes}
        </div>
      )}

      {!selectMode && canExpand && expanded && (
        <>
          <ExpandedDetails item={item} />
          <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
            <EconomicsButton dealId={item.id} />
          </div>
        </>
      )}

      {!selectMode && canExpand && (
        <div className="mt-1 text-[11px] text-tg-hint/70 select-none">
          {expanded ? "▲ свернуть" : "▼ подробнее"}
        </div>
      )}

      {!selectMode && (
        <div
          className="mt-2.5 flex flex-wrap gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {item.state === "awaiting_payment" &&
            me.permissions.payments_write && (
              <button
                disabled={busy}
                onClick={() => onAction(item.id, "mark_paid")}
                className="px-3 py-1.5 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
              >
                💳 Оплачено
              </button>
            )}
          {item.state === "awaiting_payment" &&
            !me.permissions.payments_write && (
              <span className="text-xs text-tg-hint">⌛ Ждёт казначея</span>
            )}
          {item.state === "awaiting_confirm" &&
            me.permissions.payments_confirm && (
              <>
                <button
                  disabled={busy}
                  onClick={() => onAction(item.id, "confirm")}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm disabled:opacity-50"
                >
                  ✅ Есть в выписке
                </button>
                <button
                  disabled={busy}
                  onClick={() => onAction(item.id, "deny")}
                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50"
                >
                  ❌ Нет
                </button>
              </>
            )}
          {item.state === "awaiting_confirm" &&
            !me.permissions.payments_confirm && (
              <span className="text-xs text-tg-hint">🕓 Ждёт бухгалтера</span>
            )}
        </div>
      )}
      </div>
    </div>
  );
}

function PaymentStatePill({ state }: { state: string }) {
  if (state === "awaiting_payment")
    return <StatusPill tone="warning">Ждёт оплаты</StatusPill>;
  if (state === "awaiting_confirm")
    return <StatusPill tone="info">У бухгалтера</StatusPill>;
  return <StatusPill tone="success">Оплачено</StatusPill>;
}

function ExpandedDetails({ item }: { item: PaymentItem }) {
  const rows: Array<[string, string]> = [];
  if (item.fee_percent != null)
    rows.push(["% вознаграждения", `${item.fee_percent.toFixed(4)} %`]);
  if (item.fixed_commission_amount != null)
    rows.push([
      "Фикс. комиссия",
      `${fmtAmount(item.fixed_commission_amount)} ${
        item.fixed_commission_currency || ""
      }`.trim(),
    ]);
  if (item.rate != null)
    rows.push([
      "Курс фиксации",
      `${item.rate.toFixed(4)}${
        item.rate_source ? ` (${item.rate_source})` : ""
      }`,
    ]);
  if (item.operator_name)
    rows.push(["Оператор", item.operator_name]);
  if (item.company_name)
    rows.push(["Компания", item.company_name]);
  if (item.bank_name)
    rows.push(["Банк компании", item.bank_name]);
  if (item.incoming_rub_bank_name)
    rows.push(["Банк поступления RUB", item.incoming_rub_bank_name]);
  if (item.deal_type)
    rows.push(["Тип сделки", item.deal_type]);
  if (item.deal_date)
    rows.push(["Создана", fmtRefDate(item.deal_date)]);
  if (item.planned_fix_date && item.planned_fix_date !== item.deal_date)
    rows.push(["План. дата фиксации", fmtRefDate(item.planned_fix_date)]);
  if (item.instruction_received_at)
    rows.push(["Поручение получено", fmtDateTime(item.instruction_received_at)]);
  if (item.payment_sent_at)
    rows.push(["Оплачено (отметка)", fmtDateTime(item.payment_sent_at)]);
  if (item.status)
    rows.push(["Статус", item.status]);

  if (rows.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-tg-secondaryBg">
      <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1.5">
        Детали заявки
      </div>
      <dl className="text-xs grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-tg-hint">{k}</dt>
            <dd className="text-tg-text break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtAmount(n: number): string {
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function TotalsList({
  totals,
  bold = false,
}: {
  totals: Record<string, number>;
  bold?: boolean;
}) {
  const keys = Object.keys(totals).sort();
  if (keys.length === 0) {
    return <div className="text-sm text-tg-hint">—</div>;
  }
  return (
    <ul className="space-y-0.5">
      {keys.map((k) => (
        <li
          key={k}
          className={
            "flex items-baseline justify-between gap-3 text-sm " +
            (bold ? "font-semibold text-tg-text" : "font-medium text-tg-text")
          }
        >
          <span className="text-tg-hint font-normal">{k}</span>
          <span className="tabular-nums">{fmtAmount(totals[k])}</span>
        </li>
      ))}
    </ul>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
}

function fmtRefDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit", month: "2-digit", year: "2-digit",
  });
}


/** Кнопка «💼 Экономика» в раскрытой карточке заявки. По нажатию
 *  открывает модал с расчётом (Batch6 хвост, по запросу клиента). */
function EconomicsButton({ dealId }: { dealId: number }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-1.5 rounded-lg bg-tg-sectionBg text-xs font-medium"
      >
        💼 Экономика заявки
      </button>
      {open && (
        <DealEconomicsModal dealId={dealId} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
